<template>
    <section class="h-full flex">
        <!-- Index. -->
        <div class="transition-[width] duration-500 relative"
             :class="widthClass()"
             @mouseover="collapseVisible = true"
             @mouseleave="collapseVisible = false">
            <div
                v-if="collapsable && this.selectedItem"
                class="absolute top-10 z-20 p-2 right-0 transition-all duration-500"
                :class="(collapseVisible || collapsed ? 'opacity-100' : 'opacity-0') + (collapsed ? ' -mr-10' : ' -mr-6')">
                <div
                    class="mx-auto flex items-center justify-center h-7 w-7 rounded-full bg-gray-100 border-2 border-gray-300 hover:bg-blue-100"
                    @click="toggleCollapse">
                    <i
                        class="fa text-gray-500 text-sm"
                        :class="collapsed ? 'fa-arrow-right' : 'fa-arrow-left'"
                    ></i>
                </div>
            </div>
            <div class="transition-[width] duration-500 relative"
                 :class="(opened ? 'overflow-y-visible' : 'overflow-hidden') + widthClass()">
                <div class="flex-shrink-0 flex-col bg-white border-r sm:flex sm:w-96 w-full flex"
                     :class="{ hidden: !viewingNavigation }">
                    <!-- Index header. -->
                    <div class="flex-shrink-0 p-6 border-b border-gray-200 -mb-px">
                        <slot name="new-button" v-bind="scope">
                            <div class="flex items-center">
                                <h1
                                    v-text="title"
                                    class="text-lg font-medium text-gray-900"
                                ></h1>
                                <ui-button-new
                                    v-if="!readOnly"
                                    small
                                    class="ml-auto hidden sm:inline-flex"
                                    icon="fas fa-plus"
                                    @click.prevent="createNew()"
                                >New
                                </ui-button-new>
                            </div>
                            <p
                                v-if="subtitle"
                                v-text="subtitle"
                                class="mt-1 text-sm text-gray-600"
                            ></p>
                            <ui-button-new
                                v-if="!readOnly"
                                class="sm:hidden mt-4"
                                icon="fas fa-plus"
                                @click.prevent="createNew()"
                            >New
                            </ui-button-new>
                        </slot>
                        <slot name="filters" v-bind="scope"></slot>
                    </div>

                    <!-- Index loading state. -->
                    <aside v-if="loadingIndex">
                        <slot name="loading-index" v-bind="scope">
                            <div class="flex justify-center items-center mx-4 my-8 text-gray-500 space-x-3">
                                <svg class="animate-spin text-gray-400 w-5 h-5"
                                     xmlns="http://www.w3.org/2000/svg"
                                     fill="none"
                                     viewBox="0 0 24 24">
                                    <circle
                                        class="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="4"
                                    ></circle>
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                <p>Loading...</p>
                            </div>
                        </slot>
                    </aside>

                    <!-- Index empty state. -->
                    <aside v-else-if="items.length === 0">
                        <slot name="no-results" v-bind="scope">
                            <div class="mx-4 my-8 text-gray-500 text-center">
                                No results...
                            </div>
                        </slot>
                    </aside>

                    <!-- Index grouped results. -->
                    <nav v-else-if="groupBy" class="flex-1 min-h-0 overflow-y-auto relative">
                        <div v-for="group in Object.keys(groupedItems)"
                             :key="group"
                             class="relative">
                            <div
                                class="z-10 sticky top-0 border-t border-b -mb-px border-gray-200 bg-gray-100 px-6 py-1 text-sm font-medium text-gray-500">
                                <h3>{{ group }}</h3>
                            </div>
                            <ul class="relative z-0">
                                <li v-for="item in groupedItems[group]"
                                    :key="item.key()"
                                    :id="'i-' + item.key()">
                                    <slot name="list-item"
                                          v-bind="scope"
                                          :item="item"
                                          :active="isSelected(item)"
                                          :select="getSelectFunctionFor(item)"></slot>
                                </li>
                            </ul>
                        </div>
                    </nav>

                    <!-- Index ungrouped results. -->
                    <nav v-else class="flex-1 min-h-0 overflow-y-auto">
                        <div v-for="item in pipedItems"
                             :key="item.key()"
                             :id="'i-' + item.key()">
                            <slot name="list-item"
                                  v-bind="scope"
                                  :item="item"
                                  :active="isSelected(item)"
                                  :select="getSelectFunctionFor(item)"></slot>
                        </div>
                    </nav>
                </div>
            </div>
        </div>

        <!-- Details. -->
        <div class="relative sm:block flex-1 bg-white overflow-y-auto"
             :class="{ hidden: !viewingMain }"
             id="ui-crud-main-body">
            <div class="sm:hidden pt-5 pl-5 pr-5 -mb-8">
                <a href="#"
                   @click.prevent="setShowNav(true)"
                   class="text-brand font-bold text-lg mb-4 block sm:hidden">
                    <i class="fas fa-chevron-square-left"></i> Back To Nav </a>
            </div>

            <!-- Details loading state. -->
            <template v-if="loadingDetails">
                <slot name="loading-details" v-bind="scope">
                    <div
                        class="z-30 absolute inset-0 bg-white bg-opacity-95 flex flex-col justify-center items-center mx-4 my-8 text-gray-500 space-y-3">
                        <svg class="animate-spin text-gray-400 w-10 h-10"
                             xmlns="http://www.w3.org/2000/svg"
                             fill="none"
                             viewBox="0 0 24 24">
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <p class="text-xl">Loading...</p>
                    </div>
                </slot>
            </template>

            <!-- Create. -->
            <div v-if="createMode">
                <slot name="create" v-bind="scope">CREATE PAGE</slot>
            </div>

            <!-- Edit. -->
            <div v-else-if="editMode" :key="selectedItem.key()">
                <slot name="edit" v-bind="scope">EDIT PAGE</slot>
            </div>

            <!-- Show. -->
            <div v-else :key="selectedItem.key()">
                <slot name="show" v-bind="scope">SHOW PAGE</slot>
            </div>
        </div>
    </section>
</template>

<script>
import {debounce} from "lodash";
import UiButtonNew from "./UiButtonNew.vue";

export default {
    components: {UiButtonNew},
    props: {
        title: String,
        subtitle: String,
        fetch: Function,
        groupBy: Function | String,
        pipeWith: Function,
        create: Function,
        readOnly: Boolean,
        collapsable: {type: Boolean, default: false},
    },
    data() {
        return {
            loadingIndex: true,
            loadingDetails: false,
            items: [],
            selectedItem: null,
            editMode: false,
            page: 1,
            showNav: true,
            debouncedFetchItems: debounce(this.fetchItems, 250),
            loading: true,
            landing: true,
            collapseVisible: false,
            collapsed: false,
            opened: true,
        };
    },
    computed: {
        createMode() {
            return !this.selectedItem;
        },
        viewingNavigation() {
            return this.showNav;
        },
        viewingMain() {
            return !this.viewingNavigation;
        },
        showCreateButton() {
            return !this.createMode && !this.editMode;
        },
        pipedItems() {
            return this.pipeWith ? this.pipeWith(this.items) : this.items;
        },
        groupedItems() {
            return this.groupBy
                ? _.groupBy(this.pipedItems, this.groupBy)
                : this.pipedItems;
        },
        scope() {
            return {
                // Data.
                items: this.items,
                pipedItems: this.pipedItems,
                groupedItems: this.groupedItems,
                selectedItem: this.selectedItem,
                showCreateButton: this.showCreateButton,
                page: this.page,
                editMode: this.editMode,
                loadingIndex: this.loadingIndex,
                loadingDetails: this.loadingDetails,

                // Functions.
                setEditMode: this.setEditMode,
                setLoadingDetails: this.setLoadingDetails,
                createNew: this.createNew,
                refresh: this.fetchItems,
                debouncedRefresh: this.debouncedFetchItems,
                isSelected: this.isSelected,
                selectItem: this.selectItem,
                addItem: this.addItem,
                removeItem: this.removeItem,
            };
        },
    },
    methods: {
        toggleCollapse() {
            this.collapsed = !this.collapsed;
            if (this.collapsed) {
                this.opened = false;
            } else {
                this.opened = false;
                setTimeout(() => (this.opened = true), 490);
            }
        },
        widthClass() {
            return (
                " sm:flex w-full flex " +
                (this.collapsable && this.collapsed && this.selectedItem
                    ? "sm:w-0"
                    : "sm:w-96") +
                (!this.viewingNavigation ? " hidden" : "")
            );
        },
        setEditMode(editMode = true, sortFunction = null) {
            this.editMode = editMode;
            if (sortFunction) {
                sortFunction(this.items);
            }
        },
        setLoadingDetails(loadingDetails = true) {
            this.loadingDetails = loadingDetails;
        },
        setLoadingIndex(loadingIndex = true) {
            this.loadingIndex = loadingIndex;
        },
        setShowNav(showNav = true) {
            this.showNav = showNav;
        },
        isSelected(item) {
            return this.selectedItem?.key() === item.key();
        },
        getSelectFunctionFor(item) {
            return (editMode = false) => this.selectItem(item, editMode);
        },
        selectItem(item, editMode = false) {
            this.selectedItem = !item ? null : item;
            this.editMode = editMode;
            this.showNav = false;
        },
        createNew() {
            this.editMode = false;
            this.selectedItem = null;
            this.showNav = false;
            if (this.create) {
                this.create();
            }
        },
        addItem(item, index = null, sortFunction = null) {
            index === null
                ? this.items.push(item)
                : this.items.splice(index, 0, item);

            if (sortFunction) {
                sortFunction(this.items);
            }
            this.selectItem(item);
        },
        removeItem(item) {
            const index = this.items.findIndex((i) => i.key() === item.key());
            if (index >= 0) {
                this.items.splice(index, 1);
            }
            if (this.isSelected(item)) {
                this.selectItem(null);
            }
        },
        async fetchItems() {
            this.loadingIndex = true;
            this.selectedItem = null;
            try {
                this.items = await this.fetch(this.page);
                if (this.readOnly && this.items.length > 0) {
                    this.selectItem(this.items[0]);
                }
            } finally {
                this.loadingIndex = false;
            }
            if (this.landing) {
                let urlParams = new URLSearchParams(window.location.search);
                let id = urlParams.get("goto");
                if (id) {
                    let params = id.split("|");
                    let item = this.items.find((i) => i[params[0]] == params[1]);
                    if (item) {
                        this.$nextTick(() => {
                            document.getElementById("i-" + item.key()).scrollIntoView({
                                behavior: "instant",
                                block: "center",
                                inline: "nearest",
                            });
                            this.selectItem(item);
                        });
                    }
                    urlParams.delete("goto")
                    if (history.replaceState) {
                        let searchString = urlParams.toString().length > 0 ? '?' + urlParams.toString() : '';
                        let newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + searchString + window.location.hash;
                        history.replaceState(null, '', newUrl);
                    }
                }

                this.landing = false;
            }
        },
    },
    mounted() {
        $("#app-content").css("padding", 0);
        this.fetchItems();
    },
}
;
</script>
